import * as React from 'react'

import { spacing, styled } from '@thg-commerce/enterprise-theme'

const CountdownWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  font-size: ${(props) =>
    props.color ||
    props.theme.widgets.stripBanner?.countdownTimer?.fontSize ||
    ''};
  font-weight: ${(props) =>
    props.theme.widgets.stripBanner?.countdownTimer?.fontWeight || 'normal'};
`

const Segment = styled.span<{ color?: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 36px;
  border: 0.5px solid
    ${(props) => props.color || props.theme.widgets.stripBanner.font.textColor};
  border-radius: ${spacing(0.5)};
  box-sizing: border-box;
  padding: ${spacing(0.25)} ${spacing(1)};
  color: ${(props) =>
    props.color || props.theme.widgets.stripBanner.font.textColor};
`

const Separator = styled.span<{ color?: string }>`
  margin: 0 ${spacing(0.5)};
  color: ${(props) =>
    props.color || props.theme.widgets.stripBanner.font.textColor};
`

export interface CountdownTimerProps {
  timezoneOffset?: string
  mondayCutoff?: string
  tuesdayCutoff?: string
  wednesdayCutoff?: string
  thursdayCutoff?: string
  fridayCutoff?: string
  saturdayCutoff?: string
  sundayCutoff?: string
  countdownSeparatorColor?: string
  countdownTimerColor?: string
}

export const CountdownTimer: React.FC<CountdownTimerProps> = ({
  timezoneOffset = '0',
  mondayCutoff,
  tuesdayCutoff,
  wednesdayCutoff,
  thursdayCutoff,
  fridayCutoff,
  saturdayCutoff,
  sundayCutoff,
  countdownSeparatorColor,
  countdownTimerColor,
}) => {
  const tzOffset = parseInt(timezoneOffset, 10) || 0

  const dailyCutoff = React.useMemo(
    () => ({
      0: sundayCutoff,
      1: mondayCutoff,
      2: tuesdayCutoff,
      3: wednesdayCutoff,
      4: thursdayCutoff,
      5: fridayCutoff,
      6: saturdayCutoff,
    }),
    [
      sundayCutoff,
      mondayCutoff,
      tuesdayCutoff,
      wednesdayCutoff,
      thursdayCutoff,
      fridayCutoff,
      saturdayCutoff,
    ],
  )

  const parseCutoff = React.useCallback((cutoff: string) => {
    const [h, m] = cutoff.split(':').map(Number)
    return { h, m }
  }, [])

  const computeTimeLeft = React.useCallback((): number | null => {
    const now = new Date()
    const today = now.getDay()
    const cutoff = dailyCutoff[today]
    if (!cutoff) return null

    const { h, m } = parseCutoff(cutoff)
    const target = new Date(now)
    target.setHours(h - tzOffset, m, 0, 0)

    const diff = target.getTime() - now.getTime()
    return diff > 0 ? diff : null
  }, [dailyCutoff, parseCutoff, tzOffset])

  const [timeLeft, setTimeLeft] = React.useState<number | null>(computeTimeLeft)

  React.useEffect(() => {
    const tick = () => setTimeLeft(computeTimeLeft())
    tick()

    const now = Date.now()
    const delay = 1000 - (now % 1000)

    const timeoutId = window.setTimeout(() => {
      tick()
      const intervalId = window.setInterval(tick, 1000)
      return () => clearInterval(intervalId)
    }, delay)

    return () => clearTimeout(timeoutId)
  }, [computeTimeLeft])

  if (timeLeft === null) return null

  const pad = (n: number) => n.toString().padStart(2, '0')
  const hrs = pad(Math.floor(timeLeft / 3600000) % 24)
  const mins = pad(Math.floor(timeLeft / 60000) % 60)
  const secs = pad(Math.floor(timeLeft / 1000) % 60)

  return (
    <CountdownWrapper data-testid="countdown-timer">
      <Segment color={countdownTimerColor}>{hrs}</Segment>
      <Separator color={countdownSeparatorColor}>:</Separator>
      <Segment color={countdownTimerColor}>{mins}</Segment>
      <Separator color={countdownSeparatorColor}>:</Separator>
      <Segment color={countdownTimerColor}>{secs}</Segment>
    </CountdownWrapper>
  )
}
